import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { EmptyState, Panel, Skeleton, StatusBadge, T, money, MobileCards } from "@/components/admin/ui";

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    adminApi.get("/payments", { params: { page, per_page: 30 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les paiements"));
  }, [page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 30)) : 1;

  return (
    <div className="space-y-5" data-testid="admin-payments">
      <div>
        <h1 className={T.pageTitle}>Paiements</h1>
        <p className="mt-1 text-sm text-slate">Paiements en ligne (SumUp) et encaissements manuels sur factures — aucune donnée de carte n'est jamais stockée chez MILES.</p>
      </div>

      <Panel testId="payments-list" padded={false}>
        {!data && <div className="p-5"><Skeleton rows={6} /></div>}
        {data && data.payments.length === 0 && <EmptyState title="Aucun paiement" hint="Les transactions SumUp apparaîtront ici." testId="payments-empty" />}
        {data && data.payments.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/60">
                    <th className="px-5 pb-2 pt-4">Date</th>
                    <th className="px-3 pb-2 pt-4">Réservation</th>
                    <th className="px-3 pb-2 pt-4">Client</th>
                    <th className="px-3 pb-2 pt-4">Statut</th>
                    <th className="px-5 pb-2 pt-4 text-right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p, i) => {
                    const isInvoice = p.source === "invoice";
                    return (
                    <tr key={p.id || `${p.tracking_code}-${i}`} className="transition-colors hover:bg-miles-ice/30">
                      <td className="px-5 py-3.5 font-mono text-[13px] text-slate">{(p.updated_at || p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-3.5">
                        {isInvoice ? (
                          <div>
                            <span className="font-mono text-[13px] font-semibold text-miles" data-testid={`payment-${p.id}`}>{p.invoice_number}</span>
                            <p className="text-[11px] capitalize text-slate">{p.method}{p.reference ? ` · ${p.reference}` : ""}</p>
                          </div>
                        ) : (
                          <>
                            <Link to={`/admin/app/bookings/${p.tracking_code}`} className="font-mono text-[13px] font-semibold text-miles hover:underline" data-testid={`payment-${p.tracking_code}`}>{p.tracking_code}</Link>
                            {p.refunds.length > 0 && <p className="font-mono text-[11px] text-red-600">−{money(p.refunds.reduce((s, r) => s + r.amount_chf, 0))} remboursés</p>}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-sm text-ink">
                        {isInvoice ? (p.customer?.name || "—") : (p.customer ? `${p.customer.first_name || ""} ${p.customer.last_name || ""}`.trim() : p.email)}
                      </td>
                      <td className="px-3 py-3.5"><StatusBadge status={isInvoice ? "succeeded" : (p.payment_status === "completed" ? "succeeded" : p.payment_status)} />
                        {!isInvoice && <p className="mt-1 text-[11px] font-medium capitalize text-slate" data-testid={`payment-provider-${p.tracking_code}`}>{p.payment_provider === "sumup" ? "SumUp" : "Stripe"}</p>}
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono text-sm font-medium text-ink">
                        {isInvoice ? money(p.amount_chf) : money((p.amount || 0) / 100)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <MobileCards testId="payments-cards-mobile">
              {data.payments.map((p, i) => {
                const isInvoice = p.source === "invoice";
                return (
                  <div key={p.id || `${p.tracking_code}-${i}-m`}
                    className="rounded-2xl border border-ink/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                    <div className="flex items-center justify-between gap-2">
                      {isInvoice ? (
                        <span className="font-mono text-[13px] font-semibold text-miles" data-testid={`payment-m-${p.id}`}>{p.invoice_number}</span>
                      ) : (
                        <Link to={`/admin/app/bookings/${p.tracking_code}`} className="font-mono text-[13px] font-semibold text-miles" data-testid={`payment-m-${p.tracking_code}`}>{p.tracking_code}</Link>
                      )}
                      <StatusBadge status={isInvoice ? "succeeded" : (p.payment_status === "completed" ? "succeeded" : p.payment_status)} />
                    </div>
                    <p className="mt-1.5 text-sm text-ink">
                      {isInvoice ? (p.customer?.name || "—") : (p.customer ? `${p.customer.first_name || ""} ${p.customer.last_name || ""}`.trim() : p.email)}
                    </p>
                    <p className="mt-0.5 text-[11px] capitalize text-slate">
                      {isInvoice ? `${p.method}${p.reference ? ` · ${p.reference}` : ""}` : (p.payment_provider === "sumup" ? "SumUp" : "Stripe")}
                    </p>
                    <div className="mt-3 flex items-end justify-between">
                      <span className="font-mono text-[11px] text-slate">{(p.updated_at || p.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className="font-mono text-[15px] font-semibold text-ink">{isInvoice ? money(p.amount_chf) : money((p.amount || 0) / 100)}</span>
                    </div>
                  </div>
                );
              })}
            </MobileCards>
            <div className="flex items-center justify-between px-5 pb-4 pt-2 text-sm">
              <span className={T.meta}>{data.total} transaction(s) · page {page}/{totalPages}</span>
              <div className="flex gap-1.5">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
                  className="h-8 w-8 rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles disabled:opacity-35">←</button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"
                  className="h-8 w-8 rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles disabled:opacity-35">→</button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
